import { useCallback, useEffect, useMemo, useState } from "react";

export type AsyncState<T> =
  | { status: "loading"; reload: () => void }
  | { status: "ready"; data: T; reload: () => void }
  | { status: "error"; error: Error; reload: () => void };

export function useApi<T>(
  loader: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
): AsyncState<T> {
  const [state, setState] = useState<
    { status: "loading" } | { status: "ready"; data: T } | { status: "error"; error: Error }
  >({ status: "loading" });
  const [nonce, setNonce] = useState(0);

  // Refetch on demand. Used for polling/refresh after a mutation; keeps the
  // currently-rendered data visible during the refetch (no loading flash).
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are caller-supplied and `loader` is intentionally re-created each render; `nonce` drives the manual reload.
  useEffect(() => {
    let cancelled = false;
    // Only show the loading state on a genuine first fetch; a reload keeps the
    // last good data on screen so a 3s status poll doesn't blank the grid.
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
    loader()
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ status: "error", error });
      });
    return () => {
      cancelled = true;
    };
  }, [...deps, nonce]);

  // WICHTIG: referenz-stabil halten. Konsumenten nutzen den Rückgabewert als
  // useEffect-/useMemo-Dependency (z. B. AdminAppDetail `[status]`); gäbe der Hook
  // bei jedem Render ein frisches Objekt zurück, würden diese Effekte bei jedem
  // Render feuern — das löste einen Endlos-Render-Loop (setForm) und einen
  // Logs-Fetch-Sturm aus. Das Objekt ändert sich daher nur, wenn sich `state`
  // ändert (`reload` ist via useCallback stabil).
  return useMemo(() => ({ ...state, reload }), [state, reload]);
}
